import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SettingsAddress } from 'icreate/common/models/alarm-group';
import { SensorTagSlot } from 'icreate/common/models/sensor-cable-tag';
import { Vessel } from 'icreate/common/models/vessel';
import { DutyEngineerView } from 'icreate/common/models/duty-engineer';
import { getValidationErrors, ValidationError } from 'icreate/common/validators';
import { inputAddressValidator } from 'icreate/common/validators/input-address/input-address-validator';

@Component({
    selector: 'app-set-input-address-dialog',
    templateUrl: './set-input-address-dialog.component.html',
    styleUrls: ['./set-input-address-dialog.component.scss'],
    standalone: false,
})
export class SetInputAddressDialogComponent implements OnInit {
    readonly FU_NO_SELECTION = 'No selection';

    isLoading = false;
    vesselSlots: SensorTagSlot[] = [];
    settingAddress: SettingsAddress | null = null;

    isFormTouched = false;
    hasError = false;

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            engineer: DutyEngineerView;
            vessel: Vessel;
            vesselSlots: SensorTagSlot[];
        },
        private dialogRef: MatDialogRef<SetInputAddressDialogComponent>
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        this.vesselSlots = this.data.vesselSlots ?? [];
        this.settingAddress = this.parseInputAddress(this.data.engineer.inputAddress);
        this.isLoading = false;
    }

    onSelectorChange(address: SettingsAddress) {
        this.settingAddress = address;
        this.isFormTouched = true;
        const errors: ValidationError[] = getValidationErrors(inputAddressValidator(), this.settingAddress);
        this.hasError = errors.length > 0;
    }

    cancel() {
        this.dialogRef.close(null);
    }

    save() {
        if (this.hasError) return;
        const address = this.settingAddress;
        const matchingSlot = address
            ? this.vesselSlots.find(
                  s =>
                      s.fcuNum === address.fcuNum &&
                      (s.fuNum ?? null) === (address.fuNum ?? null) &&
                      s.no === address.slotNum
              )
            : null;

        this.dialogRef.close({
            inputAddress: this.formatInputAddress(address),
            fcuId: matchingSlot?.fcuId ?? null,
            fuId: matchingSlot?.fuId ?? null,
            slotId: matchingSlot?.slotId ?? null,
        });
    }

    private parseInputAddress(inputAddress: string): SettingsAddress | null {
        if (!inputAddress) return null;
        const parts = inputAddress.split(', ');
        const fcuNo = parts[0] ?? '';
        const fuNo = parts[1] ?? '';
        const slot = parts[2] ?? '';
        const terminal = parts[3] ?? '';

        const fcuNum = fcuNo ? parseInt(fcuNo.replace('FCU', '')) : NaN;
        if (isNaN(fcuNum)) return null;

        return {
            fcuNum,
            fuNum: fuNo && fuNo !== this.FU_NO_SELECTION ? parseInt(fuNo.replace('FU', '')) : null,
            slotNum: slot ? parseInt(slot.replace('Slot ', '')) : NaN,
            terminal: terminal ? parseInt(terminal.replace('Terminal ', '')) : NaN,
        };
    }

    private formatInputAddress(address: SettingsAddress | null): string {
        if (!address || isNaN(address.fcuNum)) return '';
        const fcuPart = `FCU${address.fcuNum}`;
        const fuPart = address.fuNum != null ? `FU${address.fuNum}` : this.FU_NO_SELECTION;
        const slotPart = address.slotNum != null ? `Slot ${String(address.slotNum).padStart(2, '0')}` : '';
        const terminalPart = address.terminal != null ? `Terminal ${String(address.terminal).padStart(2, '0')}` : '';
        return [fcuPart, fuPart, slotPart, terminalPart].filter(p => p).join(', ');
    }
}
